import React, { useRef, useEffect, useState } from 'react';
import { Send, Trash2, Settings } from 'lucide-react';
import { useChat } from '../../hooks/useChat';

interface ChatPanelProps {
  /** Unique identifier for this conversation (e.g. "hq", "office:kaalvoet", "agent:KAELO") */
  conversationId: string;
  /** Display name for the agent in this chat */
  agentName: string;
  /** Webhook URL to POST messages to (from Settings) */
  webhookUrl?: string;
  /** GET endpoint to poll for new messages (from Settings) */
  pollUrl?: string;
  /** If true, render in compact mode (used inside AgentWorkstation) */
  compact?: boolean;
}

/**
 * ChatPanel — reusable chat component used in HQ, office screens, and agent workstations.
 * Sends messages via POST to webhookUrl and polls pollUrl every 3 seconds.
 * Shows "Configure endpoint in Settings" when webhookUrl is not set.
 */
export default function ChatPanel({
  conversationId,
  agentName,
  webhookUrl,
  pollUrl,
  compact = false,
}: ChatPanelProps) {
  const { messages, loading, sending, sendMessage, clearHistory } = useChat({
    conversationId,
    webhookUrl,
    pollUrl,
    agentName,
  });
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  const isConfigured = !!webhookUrl;

  return (
    <div className="flex flex-col h-full" data-testid={`chat-panel-${conversationId}`}>
      {/* Message list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && (
          <div className="flex justify-center py-4">
            <div className="h-5 w-5 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-10">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <span className="text-gray-400 text-lg font-bold">
                {agentName.charAt(0)}
              </span>
            </div>
            <p className="text-sm font-medium text-gray-500">{agentName}</p>
            <p className="text-xs text-gray-400 mt-1">Start a conversation</p>
          </div>
        )}

        {messages.map((msg) => {
          const isUser = msg.sender === 'user';
          return (
            <div
              key={msg.id}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              data-testid={`chat-message-${msg.id}`}
            >
              {!isUser && (
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 mr-2 mt-1 shrink-0">
                  {msg.sender.charAt(0).toUpperCase()}
                </div>
              )}
              <div
                className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  isUser
                    ? 'bg-gray-800 text-white rounded-tr-sm'
                    : 'bg-gray-100 text-gray-800 rounded-tl-sm border border-gray-200'
                }`}
              >
                <p>{msg.content}</p>
                <p className={`text-[10px] mt-1 ${isUser ? 'text-gray-400' : 'text-gray-400'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Not configured state */}
      {!isConfigured && (
        <div className="px-4 py-2 bg-amber-50 border-t border-amber-200">
          <div className="flex items-center gap-2 text-amber-700">
            <Settings size={13} />
            <p className="text-xs font-medium">
              Configure chat webhook URL in Settings to send messages
            </p>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 p-3 bg-gray-50 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder={isConfigured ? `Message ${agentName}...` : 'Configure endpoint in Settings'}
          disabled={sending}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:opacity-50"
          data-testid="chat-input"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="w-9 h-9 rounded-lg bg-gray-800 text-white flex items-center justify-center disabled:opacity-40 hover:bg-gray-700 transition-colors active:scale-95"
          data-testid="chat-send-button"
        >
          {sending ? (
            <div className="h-3.5 w-3.5 rounded-full border border-white/30 border-t-white animate-spin" />
          ) : (
            <Send size={14} />
          )}
        </button>
        <button
          onClick={clearHistory}
          className="w-9 h-9 rounded-lg border border-gray-200 text-gray-400 flex items-center justify-center hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Clear history"
          data-testid="chat-clear-button"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
